import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import underscore from 'underscore';
import { GetCustomerEditAtom } from '../store/customerAtom';
import GeneralSelect from './GeneralSelect';

function AddCustomerAddress(props) {
  const customerToEdit = GetCustomerEditAtom();
  const isMountedRef = useRef(true);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      Billing_Address1: '',
      Billing_Address2: '',
      Billing_Attention: '',
      Billing_Zip_Code: '',
      Billing_Fax_Number: '',
      Billing_Country_Id: '',
      Billing_State_Id: '',
      Billing_Area_Id: '',
      Shipping_Attention: '',
      Shipping_Zip_Code: '',
      Shipping_Address1: '',
      Shipping_Address2: '',
      Shipping_Fax_Number: '',
      Shipping_Country_Id: '',
      Shipping_State_Id: '',
      Shipping_Area_Id: '',
    },
  });

  const [stateDisplay, setStateDisplay] = useState([]);
  const [stateDisplay2, setStateDisplay2] = useState([]);
  const [displayArea, setDisplayArea] = useState([]);
  const [displayArea2, setDisplayArea2] = useState([]);
  const [isSameAddress, setIsSameAddress] = useState(false);
  
  // Add these state variables to track selected names for display
  const [billingCountryName, setBillingCountryName] = useState('');
  const [billingStateName, setBillingStateName] = useState('');
  const [billingAreaName, setBillingAreaName] = useState('');
  const [shippingCountryName, setShippingCountryName] = useState('');
  const [shippingStateName, setShippingStateName] = useState('');
  const [shippingAreaName, setShippingAreaName] = useState('');

  const { masterData } = props;

  // Track mount state
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Memoize expensive groupBy operation
  const masterDataGroup = useMemo(() => {
    if (!masterData || masterData.length === 0) return {};
    return underscore.groupBy(masterData, 'Master_Type');
  }, [masterData]);

  // Memoize derived lists
  const countryList = useMemo(() => masterDataGroup[1] || [], [masterDataGroup]);
  const stateList = useMemo(() => masterDataGroup[2] || [], [masterDataGroup]);
  const areaList = useMemo(() => masterDataGroup[3] || [], [masterDataGroup]);

  // Watch billing fields for "same as" checkbox functionality
  const billingAddress1 = watch('Billing_Address1');
  const billingAddress2 = watch('Billing_Address2');
  const billingAttention = watch('Billing_Attention');
  const billingZipCode = watch('Billing_Zip_Code');
  const billingFaxNumber = watch('Billing_Fax_Number');
  const billingAreaId = watch('Billing_Area_Id');
  const billingCountryId = watch('Billing_Country_Id');
  const billingStateId = watch('Billing_State_Id');

  // Initialize form with edit data
  useEffect(() => {
    if (customerToEdit?.Customer_Id && isMountedRef.current) {
      reset({
        Billing_Address1: customerToEdit.Billing_Address1 || '',
        Billing_Address2: customerToEdit.Billing_Address2 || '',
        Billing_Attention: customerToEdit.Billing_Attention || '',
        Billing_Zip_Code: customerToEdit.Billing_Zip_Code || '',
        Billing_Fax_Number: customerToEdit.Billing_Fax_Number || '',
        Billing_Country_Id: customerToEdit.Billing_Country_Id || '',
        Billing_State_Id: customerToEdit.Billing_State_Id || '',
        Billing_Area_Id: customerToEdit.Billing_Area_Id || '',
        Shipping_Attention: customerToEdit.Shipping_Attention || '',
        Shipping_Zip_Code: customerToEdit.Shipping_Zip_Code || '',
        Shipping_Address1: customerToEdit.Shipping_Address1 || '',
        Shipping_Address2: customerToEdit.Shipping_Address2 || '',
        Shipping_Fax_Number: customerToEdit.Shipping_Fax_Number || '',
        Shipping_Country_Id: customerToEdit.Shipping_Country_Id || '',
        Shipping_State_Id: customerToEdit.Shipping_State_Id || '',
        Shipping_Area_Id: customerToEdit.Shipping_Area_Id || '',
      });

      // Set display names
      setBillingCountryName(customerToEdit.Billing_Country_Name || '');
      setBillingStateName(customerToEdit.Billing_City_Name || '');
      setBillingAreaName(customerToEdit.Billing_Area_Name || '');
      setShippingCountryName(customerToEdit.Shipping_Country_Name || '');
      setShippingStateName(customerToEdit.Shipping_City_Name || '');
      setShippingAreaName(customerToEdit.Shipping_Area_Name || '');

      // Initialize cascading dropdowns for billing address
      if (customerToEdit.Billing_Country_Id) {
        const filteredStates = stateList.filter(state => state.Parent_Code1 === customerToEdit.Billing_Country_Id);
        setStateDisplay(filteredStates);

        if (customerToEdit.Billing_State_Id) {
          const filteredAreas = areaList.filter(area => area.Parent_Code1 === customerToEdit.Billing_State_Id);
          setDisplayArea(filteredAreas);
        }
      }

      // Initialize cascading dropdowns for shipping address
      if (customerToEdit.Shipping_Country_Id) {
        const filteredStates = stateList.filter(state => state.Parent_Code1 === customerToEdit.Shipping_Country_Id);
        setStateDisplay2(filteredStates);

        if (customerToEdit.Shipping_State_Id) {
          const filteredAreas = areaList.filter(area => area.Parent_Code1 === customerToEdit.Shipping_State_Id);
          setDisplayArea2(filteredAreas);
        }
      }
    }
  }, [customerToEdit, reset, stateList, areaList]);

  // Billing Country Selection
  const selectedBillCountry = useCallback(
    selection => {
      if (!selection?.Code) return;

      setValue('Billing_Country_Id', selection.Code, { shouldValidate: true });
      setBillingCountryName(selection.Name || selection.Description || '');
      
      // Reset dependent fields
      setValue('Billing_State_Id', null);
      setValue('Billing_Area_Id', null);
      setBillingStateName('');
      setBillingAreaName('');
      setDisplayArea([]);

      const filtered = stateList.filter(state => state.Parent_Code1 === selection.Code);
      if (isMountedRef.current) {
        setStateDisplay(filtered);
      }
    },
    [stateList, setValue]
  );

  // Billing State Selection
  const onSelectBillingState = useCallback(
    selection => {
      if (!selection?.Code) return;

      setValue('Billing_State_Id', selection.Code, { shouldValidate: true });
      setBillingStateName(selection.Name || selection.Description || '');
      
      // Reset dependent field
      setValue('Billing_Area_Id', '');
      setBillingAreaName('');

      const filtered = areaList.filter(area => area.Parent_Code1 === selection.Code);
      if (isMountedRef.current) {
        setDisplayArea(filtered);
      }
    },
    [areaList, setValue]
  );

  // Billing Area Selection
  const onBillingAreaSelect = useCallback(
    selection => {
      if (selection?.Code) {
        setValue('Billing_Area_Id', selection.Code, { shouldValidate: true });
        setBillingAreaName(selection.Name || selection.Description || '');
      }
    },
    [setValue]
  );

  // Shipping Country Selection
  const onShippingCountrySelect = useCallback(
    selection => {
      if (!selection?.Code) return;

      setValue('Shipping_Country_Id', selection.Code, { shouldValidate: true });
      setShippingCountryName(selection.Name || selection.Description || '');
      
      // Reset dependent fields
      setValue('Shipping_State_Id', null);
      setValue('Shipping_Area_Id', null);
      setShippingStateName('');
      setShippingAreaName('');
      setDisplayArea2([]);

      const filtered = stateList.filter(state => state.Parent_Code1 === selection.Code);
      if (isMountedRef.current) {
        setStateDisplay2(filtered);
      }
    },
    [stateList, setValue]
  );

  // Shipping State Selection
  const onShippingStateSelect = useCallback(
    selection => {
      if (!selection?.Code) return;

      setValue('Shipping_State_Id', selection.Code, { shouldValidate: true });
      setShippingStateName(selection.Name || selection.Description || '');
      
      // Reset dependent field
      setValue('Shipping_Area_Id', null);
      setShippingAreaName('');

      const filtered = areaList.filter(area => area.Parent_Code1 === selection.Code);
      if (isMountedRef.current) {
        setDisplayArea2(filtered);
      }
    },
    [areaList, setValue]
  );

  // Shipping Area Selection
  const onShippingAreaSelect = useCallback(
    selection => {
      if (selection?.Code) {
        setValue('Shipping_Area_Id', selection.Code, { shouldValidate: true });
        setShippingAreaName(selection.Name || selection.Description || '');
      }
    },
    [setValue]
  );

  // Handle "Same as Billing Address" checkbox
  const billingAddressAsShippingAddress = useCallback(
    ev => {
      const isChecked = ev.target.checked;
      setIsSameAddress(isChecked);

      if (isChecked) {
        // Copy all billing fields to shipping
        setValue('Shipping_Address1', billingAddress1 || '');
        setValue('Shipping_Address2', billingAddress2 || '');
        setValue('Shipping_Attention', billingAttention || '');
        setValue('Shipping_Zip_Code', billingZipCode || '');
        setValue('Shipping_Fax_Number', billingFaxNumber || '');
        setValue('Shipping_Area_Id', billingAreaId || null);
        setValue('Shipping_Country_Id', billingCountryId || null);
        setValue('Shipping_State_Id', billingStateId || null);

        // Copy display names
        setShippingCountryName(billingCountryName);
        setShippingStateName(billingStateName);
        setShippingAreaName(billingAreaName);

        // Copy the cascading dropdown lists
        setStateDisplay2(stateDisplay);
        setDisplayArea2(displayArea);
      } else {
        // Clear shipping fields
        setValue('Shipping_Address1', '');
        setValue('Shipping_Address2', '');
        setValue('Shipping_Attention', '');
        setValue('Shipping_Zip_Code', '');
        setValue('Shipping_Fax_Number', '');
        setValue('Shipping_Country_Id', null);
        setValue('Shipping_State_Id', null);
        setValue('Shipping_Area_Id', null);
        
        setShippingCountryName('');
        setShippingStateName('');
        setShippingAreaName('');
        setStateDisplay2([]);
        setDisplayArea2([]);
      }
    },
    [
      billingAddress1,
      billingAddress2,
      billingAttention,
      billingZipCode,
      billingFaxNumber,
      billingAreaId,
      billingCountryId,
      billingStateId,
      billingCountryName,
      billingStateName,
      billingAreaName,
      setValue,
      stateDisplay,
      displayArea,
    ]
  );

  // Auto-sync when "same as" is checked and billing fields change
  useEffect(() => {
    if (isSameAddress) {
      setValue('Shipping_Address1', billingAddress1 || '');
      setValue('Shipping_Address2', billingAddress2 || '');
      setValue('Shipping_Attention', billingAttention || '');
      setValue('Shipping_Zip_Code', billingZipCode || '');
      setValue('Shipping_Fax_Number', billingFaxNumber || '');
      setValue('Shipping_Area_Id', billingAreaId || null);
      setValue('Shipping_Country_Id', billingCountryId || null);
      setValue('Shipping_State_Id', billingStateId || null);
      
      setShippingCountryName(billingCountryName);
      setShippingStateName(billingStateName);
      setShippingAreaName(billingAreaName);
      setStateDisplay2(stateDisplay);
      setDisplayArea2(displayArea);
    }
  }, [
    isSameAddress,
    billingAddress1,
    billingAddress2,
    billingAttention,
    billingZipCode,
    billingFaxNumber,
    billingAreaId,
    billingCountryId,
    billingStateId,
    billingCountryName,
    billingStateName,
    billingAreaName,
    setValue,
    stateDisplay,
    displayArea,
  ]);

 
  const onSubmit = useCallback(
    formData => { 
      delete formData.Billing_Country_Id;
      delete formData.Billing_State_Id;
      delete formData.Shipping_Country_Id;
      delete formData.Shipping_State_Id;
       props.submitSection(formData)
       },
    [props]
  );

  const onErrors = useCallback(errors => {
    console.error('Form validation errors:', errors);
  }, []);

  return (
    <section className="w-full max-w-6xl mx-auto mt-6 p-6 bg-white rounded-lg shadow-md">
      <form onSubmit={handleSubmit(onSubmit, onErrors)} className="space-y-6">
        <div className="lg:flex lg:gap-8">
          {/* BILLING ADDRESS */}
          <section className="lg:w-1/2 space-y-4">
            <h3 className="font-bold text-lg text-gray-900 border-b border-gray-200 pb-2">Billing Address</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Attention</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter attention"
                    {...register('Billing_Attention')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address 1 <span className="text-red-500">*</span>
                  </label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Street 1"
                    {...register('Billing_Address1', {
                      required: 'Billing address is required',
                    })}
                  />
                  {errors?.Billing_Address1 && <p className="mt-1 text-sm text-red-600">{errors.Billing_Address1.message}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country/Region <span className="text-red-500">*</span>
                  </label>
                  <GeneralSelect
                    key={`billing-country-${billingCountryName}`}
                    list={countryList}
                    defaultVal={billingCountryName}
                    onOptionSelect={selectedBillCountry}
                  />
                  <input
                    type="hidden"
                    {...register('Billing_Country_Id', {
                      required: 'Country is required',
                    })}
                  />
                  {errors?.Billing_Country_Id && <p className="mt-1 text-sm text-red-600">{errors.Billing_Country_Id.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address 2</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Street 2"
                    {...register('Billing_Address2')}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    State <span className="text-red-500">*</span>
                  </label>
                  <GeneralSelect 
                    key={`billing-state-${billingStateName}`}
                    list={stateDisplay} 
                    defaultVal={billingStateName} 
                    onOptionSelect={onSelectBillingState} 
                  />
                  <input
                    type="hidden"
                    {...register('Billing_State_Id', {
                      required: 'State is required',
                    })}
                  />
                  {errors?.Billing_State_Id && <p className="mt-1 text-sm text-red-600">{errors.Billing_State_Id.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Zip Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter zip code"
                    {...register('Billing_Zip_Code', {
                      required: 'Zip code is required',
                      pattern: {
                        value: /^[A-Z0-9\s-]+$/i,
                        message: 'Please enter a valid zip code',
                      },
                    })}
                  />
                  {errors?.Billing_Zip_Code && <p className="mt-1 text-sm text-red-600">{errors.Billing_Zip_Code.message}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Area <span className="text-red-500">*</span>
                  </label>
                  <GeneralSelect 
                    key={`billing-area-${billingAreaName}`}
                    list={displayArea} 
                    defaultVal={billingAreaName} 
                    onOptionSelect={onBillingAreaSelect} 
                  />
                  <input
                    type="hidden"
                    {...register('Billing_Area_Id', {
                      required: 'Area is required',
                    })}
                  />
                  {errors?.Billing_Area_Id && <p className="mt-1 text-sm text-red-600">{errors.Billing_Area_Id.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fax</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter fax"
                    {...register('Billing_Fax_Number', {
                      pattern: {
                        value: /^[0-9+\-\s()]+$/,
                        message: 'Please enter a valid fax number',
                      },
                    })}
                  />
                  {errors?.Billing_Fax_Number && <p className="mt-1 text-sm text-red-600">{errors.Billing_Fax_Number.message}</p>}
                </div>
              </div>
            </div>

            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                onChange={billingAddressAsShippingAddress}
                checked={isSameAddress}
              />
              <span className="ml-2 text-sm text-gray-700">Billing address same as shipping address?</span>
            </label>
          </section>

          {/* SHIPPING ADDRESS */}
          <section className="lg:w-1/2 space-y-4">
            <h3 className="font-bold text-lg text-gray-900 border-b border-gray-200 pb-2">Shipping Address</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Attention</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter attention"
                    disabled={isSameAddress}
                    {...register('Shipping_Attention')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address 1 <span className="text-red-500">*</span>
                  </label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Street 1"
                    disabled={isSameAddress}
                    {...register('Shipping_Address1', {
                      required: 'Shipping address is required',
                    })}
                  />
                  {errors?.Shipping_Address1 && <p className="mt-1 text-sm text-red-600">{errors.Shipping_Address1.message}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country/Region <span className="text-red-500">*</span>
                  </label>
                  <GeneralSelect
                    key={`shipping-country-${shippingCountryName}`}
                    list={countryList}
                    defaultVal={shippingCountryName}
                    onOptionSelect={onShippingCountrySelect}
                    disabled={isSameAddress}
                  />
                  <input
                    type="hidden"
                    {...register('Shipping_Country_Id', {
                      required: 'Country is required',
                    })}
                  />
                  {errors?.Shipping_Country_Id && <p className="mt-1 text-sm text-red-600">{errors.Shipping_Country_Id.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address 2</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Street 2"
                    disabled={isSameAddress}
                    {...register('Shipping_Address2')}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    State <span className="text-red-500">*</span>
                  </label>
                  <GeneralSelect
                    key={`shipping-state-${shippingStateName}`}
                    list={stateDisplay2}
                    defaultVal={shippingStateName}
                    onOptionSelect={onShippingStateSelect}
                    disabled={isSameAddress}
                  />
                  <input
                    type="hidden"
                    {...register('Shipping_State_Id', {
                      required: 'State is required',
                    })}
                  />
                  {errors?.Shipping_State_Id && <p className="mt-1 text-sm text-red-600">{errors.Shipping_State_Id.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Zip Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter zip code"
                    disabled={isSameAddress}
                    {...register('Shipping_Zip_Code', {
                      required: 'Zip code is required',
                      pattern: {
                        value: /^[A-Z0-9\s-]+$/i,
                        message: 'Please enter a valid zip code',
                      },
                    })}
                  />
                  {errors?.Shipping_Zip_Code && <p className="mt-1 text-sm text-red-600">{errors.Shipping_Zip_Code.message}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Area <span className="text-red-500">*</span>
                  </label>
                  <GeneralSelect
                    key={`shipping-area-${shippingAreaName}`}
                    list={displayArea2}
                    defaultVal={shippingAreaName}
                    onOptionSelect={onShippingAreaSelect}
                    disabled={isSameAddress}
                  />
                  <input
                    type="hidden"
                    {...register('Shipping_Area_Id', {
                      required: 'Area is required',
                    })}
                  />
                  {errors?.Shipping_Area_Id && <p className="mt-1 text-sm text-red-600">{errors.Shipping_Area_Id.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fax</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter fax"
                    disabled={isSameAddress}
                    {...register('Shipping_Fax_Number', {
                      pattern: {
                        value: /^[0-9+\-\s()]+$/,
                        message: 'Please enter a valid fax number',
                      },
                    })}
                  />
                  {errors?.Shipping_Fax_Number && <p className="mt-1 text-sm text-red-600">{errors.Shipping_Fax_Number.message}</p>}
                </div>
              </div>
            </div>
          </section>
        </div>

        <div className="flex justify-end pt-4 border-t border-gray-200">
          <button
            type="submit"
            className="bg-blue-500 hover:bg-blue-600 text-white font-medium px-6 py-2 rounded-md transition-colors duration-200"
          >
            Save
          </button>
        </div>
      </form>
    </section>
  );
}

export default AddCustomerAddress;